
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from "react";
import { 
    Users, AlertTriangle, CheckCircle, Search, Shield, Plus, 
    Edit2, Trash2, Filter, MoreHorizontal, ChevronRight, 
    Database, Activity, Server, Globe, Cpu, UserCheck, ShieldAlert
} from "lucide-react";
import { User, ViewState } from "../../types";
import { ClusterStats } from "../clusters/ClusterStats";
import { NetworkMap } from "../clusters/NetworkMap";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";

interface ManagementViewProps {
    onNavigate: (view: ViewState) => void;
    onSelectUser: (user: User) => void;
}

export const ManagementView: React.FC<ManagementViewProps> = ({ onNavigate, onSelectUser }) => {
  const [search, setSearch] = useState("");
  
  const users: User[] = [
      { id: "user_1", email: "alice@store.com", name: "Alice Johnson", role: "admin", status: 'active', created_at: "2024-10-15" },
      { id: "user_2", email: "bob@support.com", name: "Bob Smith", role: "agent", status: 'active', created_at: "2024-10-12" },
      { id: "user_3", email: "charlie@pos.com", name: "Charlie Day", role: "cashier", status: 'suspended', created_at: "2024-09-01" },
      { id: "user_4", email: "diana@corp.com", name: "Diana Prince", role: "merchant", status: 'active', created_at: "2024-08-20" },
      { id: "user_5", email: "eve@store.com", name: "Eve Polastri", role: "merchant", status: 'active', created_at: "2024-08-15" },
  ];

  const handleUserClick = (user: User) => {
      onSelectUser(user);
      onNavigate('user-details');
  };

  const getRoleBadge = (role: string) => {
      const base = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider border transition-all shadow-sm";
      switch(role) {
          case 'admin': return <span className={`${base} bg-nobel-gold/10 border-nobel-gold text-nobel-gold`}>Protocol Admin</span>;
          case 'merchant': return <span className={`${base} bg-blue-500/10 border-blue-500 text-blue-600 dark:text-blue-400`}>Master Merchant</span>;
          case 'agent': return <span className={`${base} bg-purple-500/10 border-purple-500 text-purple-600 dark:text-purple-400`}>AI Agent</span>;
          case 'cashier': return <span className={`${base} bg-stone-100 dark:bg-white/5 border-stone-200 dark:border-white/10 text-stone-500`}>Terminal Cashier</span>;
          default: return <span className={`${base} bg-stone-100 dark:bg-white/5 border-stone-200 dark:border-white/10 text-stone-500`}>{role}</span>;
      }
  };

  return (
    <div className="space-y-12 animate-fade-in max-w-[1600px] mx-auto pb-24">
       
       {/* 1. Infrastructure Overview */}
       <div className="space-y-8">
           <div className="flex justify-between items-end px-1">
               <div className="space-y-1">
                    <h2 className="font-serif text-3xl text-[#0A0B0F] dark:text-white font-bold tracking-tight uppercase tracking-widest">GhostOS Infrastructure</h2>
                    <p className="text-stone-500 text-sm">Real-time telemetry from the sovereign node network.</p>
               </div>
               <div className="flex items-center gap-3">
                   <div className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center gap-2">
                       <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                       <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-widest">Network Consensus: Active</span>
                   </div>
               </div>
           </div>

           <ClusterStats />

           <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8">
                    <Card noPadding className="h-[440px] overflow-hidden border-stone-200 dark:border-white/10 shadow-xl bg-white dark:bg-[#15171C]">
                        <div className="p-6 border-b border-stone-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.01] flex justify-between items-center">
                            <div>
                                <h3 className="font-serif font-bold text-lg text-[#0A0B0F] dark:text-white">Global Routing Map</h3>
                                <p className="text-[9px] text-stone-500 uppercase font-black tracking-widest mt-0.5">Live Edge Distribution</p>
                            </div>
                            <Button variant="ghost" size="sm" className="font-bold text-[9px] uppercase tracking-widest">Expand Topology</Button>
                        </div>
                        <div className="h-full">
                            <NetworkMap />
                        </div>
                    </Card>
                </div>
                <div className="lg:col-span-4 flex flex-col gap-8">
                    <Card className="flex-1 bg-[#0A0B0F] text-white border-white/10 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity rotate-12"><Cpu size={120}/></div>
                        <h3 className="font-serif text-xl font-bold mb-6 relative z-10">Cluster Alerts</h3>
                        <div className="space-y-4 relative z-10">
                             <div className="flex items-start gap-4 p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20">
                                 <ShieldAlert size={18} className="text-amber-500 mt-0.5" />
                                 <div>
                                     <div className="text-[11px] font-black uppercase tracking-wider text-amber-500">High Latency - EU East</div>
                                     <div className="text-[10px] text-stone-400 leading-relaxed mt-1">Node #882 experiencing high RPC load. Auto-scaling protocols engaged.</div>
                                 </div>
                             </div>
                             <div className="flex items-start gap-4 p-4 bg-emerald-500/10 rounded-2xl border border-emerald-500/20">
                                 <CheckCircle size={18} className="text-emerald-500 mt-0.5" />
                                 <div>
                                     <div className="text-[11px] font-black uppercase tracking-wider text-emerald-500">Sui Mainnet Node Synced</div>
                                     <div className="text-[10px] text-stone-400 leading-relaxed mt-1">Atomic finality achieved in 340ms. Validation throughput optimal.</div>
                                 </div>
                             </div>
                        </div>
                    </Card>
                    <Card className="p-8 border-stone-200 dark:border-white/10 bg-white dark:bg-[#15171C]">
                        <h4 className="text-[10px] font-black text-stone-500 uppercase tracking-widest mb-4">Atomic Sync Stats</h4>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-stone-500">RPC Uptime</span>
                                <span className="font-mono font-bold text-emerald-500">99.998%</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-stone-500">Validator Stake</span>
                                <span className="font-mono font-bold text-[#0A0B0F] dark:text-white">1.2M GHOST</span>
                            </div>
                            <div className="pt-2">
                                <div className="w-full h-1.5 bg-stone-100 dark:bg-white/5 rounded-full overflow-hidden">
                                    <div className="w-[85%] h-full bg-nobel-gold shadow-[0_0_8px_#C5A059]"></div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
           </div>
       </div>

       {/* 2. User Management Section */}
       <div className="space-y-8">
           <div className="flex flex-col md:flex-row justify-between items-center gap-6 px-1">
              <div>
                <h2 className="font-serif text-3xl text-stone-900 dark:text-white font-bold tracking-tight uppercase tracking-widest">Sovereign Users</h2>
                <p className="text-stone-500 text-sm">Control role delegation and workspace permissions.</p>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <Button variant="secondary" size="md" className="rounded-xl shadow-xl font-black text-[10px] px-8 h-12 uppercase tracking-widest" icon={<Plus size={18} strokeWidth={3}/>}>Add Member</Button>
              </div>
           </div>

           <Card noPadding className="overflow-hidden border-stone-200 dark:border-white/10 shadow-2xl bg-white dark:bg-[#15171C]">
              {/* Filter Toolbar */}
              <div className="p-5 border-b border-stone-100 dark:border-white/5 flex flex-col lg:flex-row justify-between items-center gap-5 bg-gray-50/50 dark:bg-white/[0.01]">
                 <div className="relative w-full lg:max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                    <input 
                        type="text" 
                        placeholder="Search identities, addresses or roles..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-[#0A0B0F] border border-stone-200 dark:border-white/10 rounded-2xl focus:border-nobel-gold outline-none text-sm font-bold shadow-sm transition-all"
                    />
                 </div>
                 <div className="flex items-center gap-3 w-full lg:w-auto">
                     <button className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl text-stone-700 dark:text-stone-300 hover:border-nobel-gold transition-all text-[11px] font-black uppercase tracking-widest">
                         <Filter size={14}/> Filter
                     </button>
                     <button className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl text-stone-700 dark:text-stone-300 hover:border-nobel-gold transition-all text-[11px] font-black uppercase tracking-widest">
                         <Database size={14}/> CSV Audit
                     </button>
                 </div>
              </div>

              <div className="overflow-x-auto">
                 <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                       <tr className="bg-[#F9F8F4] dark:bg-white/5 text-stone-500 text-[10px] font-black uppercase tracking-[0.25em] border-b border-stone-200 dark:border-white/10">
                          <th className="px-8 py-5">Identity / Metadata</th>
                          <th className="px-8 py-5">Permission Level</th>
                          <th className="px-8 py-5">Node Status</th>
                          <th className="px-8 py-5">Last Handshake</th>
                          <th className="px-8 py-5 text-right">Ledger Access</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50 dark:divide-white/[0.03]">
                       {users.map((user) => (
                          <tr key={user.id} onClick={() => handleUserClick(user)} className="hover:bg-stone-50 dark:hover:bg-white/[0.03] transition-all group cursor-pointer">
                             <td className="px-8 py-6">
                                <div className="flex items-center gap-4">
                                   <div className="w-12 h-12 rounded-2xl bg-stone-900 dark:bg-white flex items-center justify-center text-sm font-black text-nobel-gold dark:text-black shadow-lg shadow-black/10 group-hover:scale-105 transition-transform duration-500">
                                      {user.name.charAt(0)}
                                   </div>
                                   <div>
                                      <div className="font-bold text-stone-900 dark:text-white text-base tracking-tight">{user.name}</div>
                                      <div className="text-[10px] font-mono text-stone-400 mt-1 uppercase tracking-tighter opacity-60">{user.email || '0x7611...b37c'}</div>
                                   </div>
                                </div>
                             </td>
                             <td className="px-8 py-6">
                                {getRoleBadge(user.role)}
                             </td>
                             <td className="px-8 py-6">
                                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                    user.status === 'active' 
                                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                                        : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                                }`}>
                                   <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'active' ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-red-500'}`}></div>
                                   {user.status === 'active' ? 'Authorized' : 'Suspended'}
                                </span>
                             </td>
                             <td className="px-8 py-6 text-xs text-stone-500 font-mono font-bold uppercase">
                                {user.status === 'active' ? 'Just now' : '02 Dec 2025'}
                             </td>
                             <td className="px-8 py-6 text-right">
                                <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                                   <button className="p-2.5 text-stone-400 hover:text-[#0A0B0F] dark:hover:text-white hover:bg-stone-100 dark:hover:bg-white/10 rounded-xl transition-all"><Edit2 size={18}/></button>
                                   <button className="p-2.5 text-stone-400 hover:text-blue-500 hover:bg-blue-500/10 rounded-xl transition-all"><UserCheck size={18}/></button>
                                   <button className="p-2.5 text-stone-400 hover:text-red-600 hover:bg-red-500/10 rounded-xl transition-all"><Trash2 size={18}/></button>
                                   <div className="w-px h-8 bg-stone-100 dark:bg-white/5 mx-2 self-center"></div>
                                   <button className="p-2.5 text-stone-400 hover:text-nobel-gold transition-all"><MoreHorizontal size={20}/></button>
                                </div>
                             </td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
              
              <div className="p-6 border-t border-stone-100 dark:border-white/5 bg-gray-50/50 dark:bg-[#0A0B0F] text-[10px] font-black uppercase tracking-widest text-stone-400 flex flex-col sm:flex-row justify-between items-center gap-4">
                 <span>Showing 1 to {users.length} of {users.length} entries</span>
                 <div className="flex gap-2">
                     <button className="px-5 py-2 border border-stone-200 dark:border-white/10 rounded-xl bg-white dark:bg-white/5 text-stone-600 dark:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-stone-50 transition-all" disabled>PREVIOUS</button>
                     <button className="px-5 py-2 border border-stone-200 dark:border-white/10 rounded-xl bg-white dark:bg-white/5 text-stone-600 dark:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-stone-50 transition-all" disabled>NEXT</button>
                 </div>
              </div>
           </Card>
       </div>

       {/* Audit Log Prompt Area */}
       <div className="pt-8 border-t border-stone-100 dark:border-white/5">
            <Card className="bg-[#0A0B0F] text-white p-10 rounded-[40px] border border-white/10 relative overflow-hidden group shadow-2xl">
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-nobel-gold/10 blur-[100px] -translate-y-1/2 translate-x-1/2 opacity-40"></div>
                <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
                    <div className="space-y-6 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[10px] font-black uppercase tracking-[0.4em] text-nobel-gold">
                            <Activity size={12} /> Real-time Audit stream
                        </div>
                        <h3 className="font-serif text-4xl font-bold tracking-tight">Need granular <br/><span className="text-stone-500 italic">access logs?</span></h3>
                        <p className="text-gray-400 max-w-md leading-relaxed font-light">
                            GhostOS provides cryptographically signed audit trails for every admin interaction. Export immutable session logs to any endpoint.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                        <Button variant="primary" className="h-16 px-10 text-xs font-black uppercase tracking-widest rounded-xl bg-white !text-black shadow-2xl shadow-white/5">View Master Log</Button>
                        <Button variant="outline" className="h-16 px-10 text-xs font-black uppercase tracking-widest rounded-xl border-white/20 text-white hover:bg-white/10">Configure Webhooks</Button>
                    </div>
                </div>
            </Card>
       </div>
    </div>
  );
};
